import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BadgeCheck, Star, MapPin, Wallet, LogOut, Package, Heart, Store, Settings, ArrowDownToLine, Loader2, ShieldCheck, FileText, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatIDR, downloadOrderInvoice, downloadWalletInvoice, isUserAdmin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { SettlementDetail } from "@/components/SettlementDetail";
import { AccountPicker } from "@/components/AccountPicker";
import { exportBuyerInvoicePdf } from "@/lib/settlementPdf";


export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const targetId = id || user?.id;
  const isOwn = !id || id === user?.id;

  const [data, setData] = useState(null);
  const [tab, setTab] = useState("listing");
  const [liked, setLiked] = useState([]);
  const [orders, setOrders] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [showWd, setShowWd] = useState(false);
  const [banks, setBanks] = useState([]);
  const [wd, setWd] = useState(null);
  const [wdLoading, setWdLoading] = useState(false);
  const [settleable, setSettleable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const closeDetail = useCallback(() => setDetailId(null), []);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadOrderInvoice = async (order) => {
    setDownloadingId(order.id);
    try {
      try {
        await downloadOrderInvoice(order.id, order.mch_order_no);
      } catch {
        await exportBuyerInvoicePdf(order);
      }
      toast.success("Invoice pembelian berhasil diunduh");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunduh invoice pembelian");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadWalletInvoice = async (e, item) => {
    e.stopPropagation();
    setDownloadingId(item.id);
    try {
      await downloadWalletInvoice(item.id, item.mch_order_no);
      toast.success(item.type === "debit" ? "Bukti penarikan berhasil diunduh" : "Bukti settlement berhasil diunduh");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunduh dokumen transaksi");
    } finally {
      setDownloadingId(null);
    }
  };

  const loadWallet = useCallback(() => {
    api.get("/wallet").then((r) => setWallet(r.data));
    api.get("/wallet/settleable").then((r) => { setSettleable(r.data); setSelected([]); });
  }, []);
  const toggleSel = (oid) => setSelected((s) => (s.includes(oid) ? s.filter((x) => x !== oid) : [...s, oid]));
  const selTotal = settleable.filter((o) => selected.includes(o.id)).reduce((a, o) => a + o.price, 0);

  const loadBanks = useCallback(async () => {
    try {
      const [b1, b2] = await Promise.all([
        api.get("/tarapay/banks", { params: { type: 1 } }),
        api.get("/tarapay/banks", { params: { type: 2 } }),
      ]);
      const list = [
        ...b1.data.map((x) => ({ ...x, account_type: "1" })),
        ...b2.data.map((x) => ({ ...x, account_type: "3" })),
      ];
      setBanks(list);
    } catch {
      setBanks([]);
    }
  }, []);

  const submitWithdraw = async () => {
    if (selected.length === 0) return toast.error("Pilih produk yang ingin dicairkan");
    if (selTotal < 10000) return toast.error("Minimal penarikan Rp 10.000");
    if (!wd) return toast.error("Pilih atau tambah rekening tujuan");
    setWdLoading(true);
    try {
      const { data } = await api.post("/wallet/withdraw", { bank_id: wd.bank_id, bank_name: wd.bank_name, account_type: wd.account_type, account_no: wd.account_no, account_name: wd.account_name, order_ids: selected });
      toast.success(`Penarikan dikirim — status ${data.status}`);
      setShowWd(false);
      loadWallet();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Penarikan gagal");
    } finally {
      setWdLoading(false);
    }
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/users/${targetId}`);
    setData(data);
  }, [targetId]);

  useEffect(() => { if (targetId) load(); }, [targetId, load]);

  useEffect(() => {
    if (!isOwn) return;
    if (tab === "liked") api.get("/products", { params: { liked_by: user.id } }).then((r) => setLiked(r.data));
    if (tab === "orders") api.get("/orders").then((r) => setOrders(r.data));
    if (tab === "wallet") { loadWallet(); loadBanks(); }
  }, [tab, isOwn, user]);

  if (!data) return <div className="py-20 text-center text-gray-400">Memuat...</div>;

  const u = data.user;
  const active = data.products.filter((p) => p.status === "active");
  const sold = data.products.filter((p) => p.status === "sold");

  const tabs = isOwn
    ? [
        { k: "listing", l: "Listing", icon: Store },
        { k: "liked", l: "Disukai", icon: Heart },
        { k: "orders", l: "Pesanan", icon: Package },
        { k: "wallet", l: "Dompet", icon: Wallet },
      ]
    : [
        { k: "listing", l: "Listing", icon: Store },
        { k: "sold", l: "Terjual", icon: Package },
      ];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header banner */}
      <div className="brand-gradient rounded-3xl p-6 text-white relative overflow-hidden">
        <div className="flex items-center gap-4 relative z-10">
          <img src={u.avatar_url} alt="" className="w-20 h-20 rounded-2xl object-cover border-4 border-white/20" />
          <div className="flex-1">
            <div className="flex items-center gap-1.5 text-xl font-extrabold">
              {u.name} {u.verified && <BadgeCheck className="w-5 h-5" />}
            </div>
            <div className="flex items-center gap-3 text-sm text-white/80 mt-1">
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-300 text-amber-300" /> {u.rating}</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {u.location}</span>
            </div>
            {u.bio && <p className="text-sm text-white/70 mt-1">{u.bio}</p>}
          </div>
          {isOwn && (
            <button data-testid="profile-logout-button" onClick={logout} className="p-2.5 rounded-full bg-white/15 hover:bg-white/25 transition">
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="flex gap-6 mt-4 relative z-10">
          <Stat label="Aktif" value={data.active_count} />
          <Stat label="Terjual" value={data.sold_count} />
        </div>
        <div className="absolute -right-6 -bottom-8 w-40 h-40 rounded-full bg-white/10" />
      </div>

      {isOwn && (user?.is_admin || isUserAdmin(user)) && (
        <div
          data-testid="profile-admin-portal-card"
          onClick={() => navigate("/admin")}
          className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 flex items-center justify-between cursor-pointer hover:shadow-sm transition"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0B4DA2] text-white flex items-center justify-center shadow-sm">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
                <span>Portal Admin & Verifikasi Toko</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#0B4DA2] text-white font-bold">Admin</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Verifikasi seller, kelola transaksi, dan pantau saldo TaraPay
              </div>
            </div>
          </div>
          <span className="text-xs font-bold text-[#0B4DA2]">
            Buka Portal →
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mt-5 overflow-x-auto no-scrollbar">
        {tabs.map((t) => (
          <button
            key={t.k}
            data-testid={`profile-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition ${tab === t.k ? "bg-[#0B4DA2] text-white border-[#0B4DA2]" : "bg-white text-gray-600 border-gray-200"}`}
          >
            <t.icon className="w-4 h-4" /> {t.l}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {(tab === "listing") && <Grid items={active} empty="Belum ada produk aktif." />}
        {(tab === "sold") && <Grid items={sold} empty="Belum ada produk terjual." />}
        {tab === "liked" && <Grid items={liked} empty="Belum ada produk yang disukai." />}

        {tab === "orders" && (
          <div className="space-y-3">
            {orders.length === 0 && <Empty text="Belum ada pesanan." />}
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:border-gray-200 transition">
                <img src={fileUrl(o.product_image)} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm line-clamp-1">{o.product_title}</div>
                  <div className="text-[#0B4DA2] font-bold text-sm">{formatIDR(o.amount)}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span>{o.mch_order_no || o.id}</span>
                    <span>•</span>
                    <span>{new Date(o.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${o.status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                    {o.status === "paid" ? "Dibayar" : "Menunggu"}
                  </span>
                  <button
                    data-testid={`download-invoice-${o.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadOrderInvoice(o);
                    }}
                    disabled={downloadingId === o.id}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-blue-200 bg-blue-50/70 hover:bg-blue-100 text-[#0B4DA2] text-xs font-semibold transition disabled:opacity-60"
                    title="Unduh Invoice Pembelian (PDF)"
                  >
                    {downloadingId === o.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FileText className="w-3.5 h-3.5" />
                    )}
                    <span>Invoice PDF</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "wallet" && wallet && (
          <div>
            <div className="p-5 rounded-2xl bg-gradient-to-br from-[#0B4DA2] to-[#1D6FD1] text-white">
              <div className="flex items-center gap-2 text-white/70 text-xs"><Wallet className="w-4 h-4" /> Saldo Penjualan</div>
              <div className="text-3xl font-extrabold mt-1" data-testid="wallet-balance">{formatIDR(wallet.balance)}</div>
              <div className="text-xs text-white/60 mt-1">Tarik dana hasil penjualan ke rekening bank / e-wallet</div>
              <button data-testid="withdraw-toggle-button" onClick={() => setShowWd(!showWd)} className="mt-3 flex items-center gap-1.5 bg-white text-[#0B4DA2] px-4 py-2 rounded-full text-sm font-semibold">
                <ArrowDownToLine className="w-4 h-4" /> Tarik Saldo
              </button>
            </div>

            {showWd && (
              <div className="mt-3 p-4 rounded-2xl bg-white border border-gray-100 space-y-3 mb-24 md:mb-0" data-testid="withdraw-form">
                <div className="font-semibold text-gray-800 text-sm">Tarik Saldo ke Rekening</div>
                <SettlePicker items={settleable} selected={selected} onToggle={toggleSel} onAll={() => setSelected(selected.length === settleable.length ? [] : settleable.map((o) => o.id))} total={selTotal} />
                {selTotal > wallet.balance && <div data-testid="settle-over-balance" className="text-xs text-red-600 bg-red-50 p-2.5 rounded-xl">Total produk dipilih melebihi saldo dompet ({formatIDR(wallet.balance)}). Kurangi pilihan produk.</div>}
                <AccountPicker banks={banks} value={wd} onSelect={setWd} />
                <button data-testid="wd-submit" onClick={submitWithdraw} disabled={wdLoading || selTotal > wallet.balance} className="w-full py-2.5 rounded-xl bg-[#0B4DA2] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                  {wdLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownToLine className="w-4 h-4" />} Tarik {formatIDR(selTotal)}
                </button>
              </div>
            )}
            <h3 className="font-semibold text-gray-800 mt-4 mb-2">Riwayat Transaksi</h3>
            {wallet.history.length === 0 ? <Empty text="Belum ada transaksi." /> : (
              <div className="space-y-2">
                {wallet.history.map((h) => (
                  <div key={h.id} data-testid={`wallet-history-${h.id}`} onClick={() => h.type === "debit" && setDetailId(h.id)} className={`flex items-center justify-between p-3 rounded-xl bg-white border border-gray-100 text-sm ${h.type === "debit" ? "cursor-pointer hover:border-[#0B4DA2]/40 transition-colors" : ""}`}>
                    <div className="flex-1 min-w-0 pr-3">
                      <div className="font-medium text-gray-800 line-clamp-1">{h.note}</div>
                      <div className="text-xs text-gray-400">{new Date(h.created_at).toLocaleString("id-ID")}{h.status ? ` • ${h.status}` : ""}</div>
                      {h.type === "debit" && <div className="text-[11px] text-[#0B4DA2] font-medium flex items-center gap-1 mt-0.5"><FileText className="w-3 h-3" /> {(h.order_ids || []).length} produk • Lihat rincian settlement</div>}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className={`font-bold ${h.type === "debit" ? "text-red-500" : "text-emerald-600"}`}>
                        {h.type === "debit" ? "-" : "+"}{formatIDR(h.amount)}
                      </span>
                      <button
                        data-testid={`download-wallet-invoice-${h.id}`}
                        onClick={(e) => handleDownloadWalletInvoice(e, h)}
                        disabled={downloadingId === h.id}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700 text-[11px] font-medium transition disabled:opacity-60"
                        title={h.type === "debit" ? "Unduh Bukti Penarikan (PDF)" : "Unduh Bukti Settlement (PDF)"}
                      >
                        {downloadingId === h.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <FileText className="w-3 h-3 text-[#0B4DA2]" />
                        )}
                        <span>{h.type === "debit" ? "Bukti WD" : "Invoice"}</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <SettlementDetail withdrawalId={detailId} onClose={closeDetail} />
          </div>
        )}
      </div>
    </div>
  );
}

const SettlePicker = ({ items, selected, onToggle, onAll, total }) => (
  <div data-testid="settle-picker">
    <div className="flex items-center justify-between text-xs mb-1.5">
      <span className="text-gray-500">Pilih produk terjual yang ingin dicairkan</span>
      {items.length > 0 && <button data-testid="settle-select-all" onClick={onAll} className="text-[#0B4DA2] font-semibold">{selected.length === items.length ? "Batal semua" : "Pilih semua"}</button>}
    </div>
    {items.length === 0 ? <div className="text-xs text-gray-400 p-3 rounded-xl bg-gray-50" data-testid="settle-empty">Belum ada penjualan yang bisa dicairkan.</div> : (
      <div className="max-h-56 overflow-y-auto space-y-1.5">
        {items.map((o) => {
          const on = selected.includes(o.id);
          return (
            <button key={o.id} data-testid={`settle-item-${o.id}`} onClick={() => onToggle(o.id)} className={`w-full flex items-center gap-2.5 p-2 rounded-xl border text-left transition-colors ${on ? "border-[#0B4DA2] bg-blue-50/60" : "border-gray-100"}`}>
              {on ? <CheckSquare className="w-4 h-4 text-[#0B4DA2] shrink-0" /> : <Square className="w-4 h-4 text-gray-300 shrink-0" />}
              <img src={fileUrl(o.product_image)} alt="" className="w-9 h-9 rounded-lg object-cover bg-gray-100" />
              <span className="flex-1 min-w-0 text-xs font-medium line-clamp-1">{o.product_title}</span>
              <span className="text-xs font-bold text-gray-800">{formatIDR(o.price)}</span>
            </button>
          );
        })}
      </div>
    )}
    <div className="flex justify-between text-sm mt-2"><span className="text-gray-500">Total dipilih ({selected.length})</span><span className="font-bold text-[#0B4DA2]" data-testid="settle-selected-total">{formatIDR(total)}</span></div>
  </div>
);

const Stat = ({ label, value }) => (
  <div><div className="text-2xl font-extrabold">{value}</div><div className="text-xs text-white/70">{label}</div></div>
);

const Grid = ({ items, empty }) =>
  items.length === 0 ? <Empty text={empty} /> : (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
    </div>
  );

const Empty = ({ text }) => <div className="text-center py-16 text-gray-400 text-sm">{text}</div>;
